import { Auth } from '@aws-amplify/auth';
import { SignIn } from 'aws-amplify-react';
import type { FieldProps } from 'formik';
import { Field, Formik } from 'formik';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import styled from 'styled-components';
import * as Yup from 'yup';

import { BrandContext } from '../context/BrandProvider';
import withHookedTheme from '../hocs/withHookedTheme';
import type { Theme } from '../theme/themes/ThemeTypes';
import ErrorLabel from '../ui/ErrorLabel';
import LoginLayout from '../ui/Layout/LoginLayout';
import LoginCard from '../ui/LoginCard';
import ThemeAwareLogo from '../ui/ThemeAwareLogo';
import { max } from '../utils/breakpoints';
import {
  buildRedirectUrl as buildDomainRedirectUrl,
  getCurrentHostPrefix as getHostPrefix,
  hasRedirected,
} from '../utils/domainRedirect';

type BrandCtxValue = {
  domainPrefix?: string;
  installerId?: string;
  brandResolving?: boolean;
};

const SignUpWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;

  ${max.md} {
    flex-direction: column;
  }

  .signup-button {
    flex-shrink: 0;

    ${max.md} {
      width: 100%;
      margin-top: 0px;
    }
  }
`;

interface LoopSignInState {
  initialVisit: boolean;
  loading?: boolean;
  [key: string]: any;
}

interface LoopSignInProps {
  authState: string;
  theme: Theme;
  [key: string]: any;
}

/**
 * Extending amplify boilerplate SignIn screen.
 */
class LoopSignIn extends SignIn<LoopSignInProps, LoopSignInState> {
  tracked: boolean = false;
  redirect: boolean = false;
  language: any;
  _validAuthStates: string[];

  // Consume BrandContext in a class component
  static contextType = BrandContext;

  /**
   * Class constructor
   * @param {LoopSignInProps} props The screen's properties
   */
  constructor(props: LoopSignInProps) {
    super(props);
    this.language = props.theme.language;
    this._validAuthStates = ['signIn', 'signedOut', 'signedUp'];
    this.onKeyUp = this.onKeyUp.bind(this);
    this.state = {
      initialVisit: true,
    };
  }

  /**
   * Handle key up events
   * @param {React.KeyboardEvent} event The keyboard event
   */
  onKeyUp(event: React.KeyboardEvent): void {
    if (event.charCode === 13) {
      super.signIn(event);
    }
  }

  /**
   * Component did mount handler
   */
  componentDidMount(): void {
    this.tracked = false;

    if (
      window.location.pathname === '/' &&
      this.state.initialVisit &&
      (this.props.authState === 'signedOut' || this.props.authState === 'signIn')
    ) {
      setTimeout(() => {
        super.changeState('signUp');
        this.setState({ initialVisit: false });
      }, 0);
    }
  }

  /**
   * Component did update handler
   * @param {LoopSignInProps} prevProps
   * @param {LoopSignInState} prevState
   * @param {unknown} snapshot
   */
  componentDidUpdate(prevProps: LoopSignInProps, prevState: LoopSignInState): void {
    if (
      (this.props.authState === 'signedUp' || this.props.authState === 'confirmSignUp') &&
      prevProps.authState !== this.props.authState &&
      this.state.initialVisit
    ) {
      this.setState({ initialVisit: false });
    }

    if (this.props.authState === 'signIn' && !this.tracked) {
      this.tracked = true;
      if (this.redirect) {
        this.redirect = false;
      }
    } else if (this.props.authState !== 'signIn') {
      this.tracked = false;
    }
  }

  /**
   * Handles form submission for sign in, including domain mismatch logic.
   * Uses BrandProvider context (domainPrefix) instead of a local helper.
   */
  handleSignInSubmit = async (
    values: { username: string; password: string },
    setSubmitting: (isSubmitting: boolean) => void
  ): Promise<void> => {
    setSubmitting(true);
    this.inputs = {
      username: values.username.toLowerCase().trim(),
      password: values.password,
    };

    try {
      await super.signIn();

      const brandCtx = this.context as BrandCtxValue;
      const expectedPrefix = brandCtx?.domainPrefix;
      const currentPrefix = getHostPrefix();

      if (!expectedPrefix || brandCtx?.brandResolving) return;

      // If we've already been redirected (query param present), do not redirect again
      if (hasRedirected()) return;

      if (expectedPrefix !== currentPrefix) {
        await Auth.signOut();
        window.location.href = buildDomainRedirectUrl(expectedPrefix, '/login', { redirected: 'signIn' });
        return;
      }
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Show loop sign in screen
   * @param {Theme} theme The theme object
   * @return {JSX.Element}
   */
  showComponent(theme: Theme): JSX.Element | null {
    if (this.props.authState !== 'signIn') return null;

    const { loading } = this.state as LoopSignInState;

    const signInValidationSchema = Yup.object({
      username: Yup.string().email('Invalid email address').required('Email is required'),
      password: Yup.string().required('Password is required'),
    });

    return (
      <LoginLayout>
        <Formik
          initialValues={{ username: '', password: '' }}
          validationSchema={signInValidationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            await this.handleSignInSubmit(values, setSubmitting);
          }}
          validateOnBlur
          validateOnChange
        >
          {({ handleSubmit, errors, isSubmitting }) => (
            <LoginCard>
              {theme.layout.showOrHideElements.auth.signIn.showCompanyLogoInCard && (
                <Row>
                  <Col className="px-lg-3 px-md-2 px-1 d-flex justify-content-center mb-3">
                    <ThemeAwareLogo className="d-sm-block" alt="Company Logo" />
                  </Col>
                </Row>
              )}
              <Card.Title as="h1" className="text-center">
                Sign in to
                <br />
                {theme.language.appName}
              </Card.Title>
              <Card.Text>
                {this.language.appName} will buy and store energy when it's cheap, and sell it back to the grid when
                it's expensive.
              </Card.Text>
              {theme.layout.showOrHideElements.auth.signIn.howDoesOptimiseWorkLink && (
                <Card.Text>
                  <a href={theme.links.howDoesItWork} rel="noreferrer" target="_blank">
                    How does {this.language.appName} work?
                  </a>
                </Card.Text>
              )}
              <Form noValidate onSubmit={handleSubmit} className="text-left">
                <Form.Group>
                  <Form.Label htmlFor="username" className="form-label">
                    Email address
                  </Form.Label>
                  <Field name="username">
                    {({ field, meta }: FieldProps) => (
                      <>
                        <Form.Control
                          {...field}
                          id="signin_username"
                          key="username"
                          type="email"
                          placeholder="Email address"
                          autoComplete="username"
                          onChange={(e) => {
                            e.target.value = e.target.value.toLowerCase().trim();
                            field.onChange(e);
                          }}
                          isInvalid={meta.touched && !!meta.error}
                          style={{ backgroundImage: 'none' }}
                        />
                        {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                      </>
                    )}
                  </Field>
                </Form.Group>
                <Form.Group>
                  <Form.Label htmlFor="password" className="form-label">
                    Password
                  </Form.Label>
                  <Field name="password">
                    {({ field, meta }: FieldProps) => (
                      <>
                        <Form.Control
                          {...field}
                          id="signin_password"
                          key="password"
                          type="password"
                          placeholder="Password"
                          isInvalid={meta.touched && !!meta.error}
                          style={{ backgroundImage: 'none' }}
                          autoComplete="current-password"
                        />
                        {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                      </>
                    )}
                  </Field>
                  <p className="mt-2">
                    <Button className="pl-0" variant="link" onClick={() => super.changeState('forgotPassword')}>
                      Forgot your password?{' '}
                    </Button>
                  </p>
                </Form.Group>
                <Form.Group>
                  <Button
                    type="submit"
                    variant="primary"
                    block
                    disabled={loading || isSubmitting || !!Object.keys(errors).length}
                  >
                    Sign In
                  </Button>
                </Form.Group>
                <Form.Group className="mb-1">
                  <p className="text-center">
                    Already have a sign in code?{' '}
                    <Button
                      className="p-0 align-baseline"
                      variant="link"
                      onClick={() => super.changeState('confirmSignUp')}
                    >
                      Verify code
                    </Button>
                  </p>
                </Form.Group>
                <Form.Group className="mb-0 mt-1">
                  <hr className="my-3" />
                  <SignUpWrapper>
                    <span className="signup-text">Don&apos;t have a {this.language.appName} account? </span>
                    <Button
                      type="button"
                      variant="outline-primary"
                      onClick={() => super.changeState('signUp')}
                      className="signup-button px-4"
                    >
                      Sign up
                    </Button>
                  </SignUpWrapper>
                </Form.Group>
              </Form>
            </LoginCard>
          )}
        </Formik>
      </LoginLayout>
    );
  }
}

export default withHookedTheme(LoopSignIn);
